import _ from 'lodash';
import axios from 'axios';
import config from '../config.js';
import storage from './storage.js';

let _instance;

const createInstance = ()=>{
    if (_instance)
        return _instance;
    const instance = axios.create({
        withCredentials: true,
        baseURL: config.API_BASE,
    });
    instance.interceptors.request.use(req=>{
        let token = storage.get('sc_token');
        if (token)
            req.headers.Authorization = `Bearer ${token}`;
        let proxyUser = storage.get('sc_proxy_user');
        if (proxyUser)
            req.headers['x-sc-proxy-user'] = proxyUser;
        return req;
    });
    instance.interceptors.response.use(res=>{
        if (res.headers.getContentType().startsWith('application/json'))
            return res.data;
        return res;
    }, async e=>{
        if (e.response.status==403&&e.response.data.code=='token_expired') {
            let userId = storage.get('sc_user');
            let refreshToken = storage.get('sc_refresh_token');
            let {token} = await api.refreshToken({userId, refreshToken});
            storage.set('sc_token', token);
            e.config.headers.Authorization = `Bearer ${token}`;
            return instance.request(e.config);
        }
        if (e.response.headers.getContentType().startsWith('application/json'))
            return Promise.reject(Object.assign(new Error(e.response.data.message), e.response.data));
        return Promise.reject(e);
    });
    return _instance = instance;
};

const api = {
    login: data=>createInstance().post('login', data),
    signup: data=>createInstance().post('signup', data),
    refreshToken: data=>createInstance().post('refresh_token', data),
    verifyEmail: data=>createInstance().post('verify_email', data),
    passwordResetRequest: email=>createInstance().post('reset_password_request', {email}),
    passwordResetConfirm: data=>createInstance().post('reset_password_confirm', data),
    getUser: ()=>createInstance().get('user'),
    updatePassword: data=>createInstance().post('update_password', data),
    updateUsername: username=>createInstance().post('update_username', {username}),
    contact: data=>createInstance().post('contact', data),
    randomCard: ()=>createInstance().get('random_card'),
    getCard: id=>createInstance().get(`card/${id}`),
    product: {
        createPaypage: data=>createInstance().post('/product/create_paypage', data),
        getPayment: (id, paymentToken)=>createInstance().get(`/product/payment/${id}`, {params: {paymentToken}}),
        getPaymentEvents: (id, paymentToken)=>new EventSource(`${config.API_BASE}product/payment/${id}/events?paymentToken=${paymentToken}`),
        ext: {
            testIpn: data=>createInstance().post('/ext/product_payment_complete', data),
        },
    },
    blog: {
        getPosts: params=>createInstance().get('/blog/posts', {params}),
        searchPosts: params=>createInstance().get('/blog/posts/search', {params}),
        getPost: (slug, preview)=>createInstance().get(`/blog/post/${slug}`, {params: {preview}}),
        getTags: ()=>createInstance().get('/blog/tags'),
        getCategories: ()=>createInstance().get('/blog/categories'),
    },
    tarotmail: {
        verifyCouponCode: params=>createInstance().get('/tarotmail/verify_coupon_code', {params}),
        createPaypage: data=>createInstance().post('/tarotmail/create_paypage', data),
        getPayment: (id, paymentToken)=>createInstance().get(`/tarotmail/payment/${id}`, {params: {paymentToken}}),
        validateStudent: id=>createInstance().get(`/tarotmail/validate_student/${id}`),
        checkEligibility: (id, name, email)=>createInstance().get('/tarotmail/check_eligibility', {params: {id, name, email}}),
        subscribe: (id, data)=>createInstance().post(`/tarotmail/${id}/subscribe`, data),
        sagieAddRound: data=>createInstance().post(`/tarotmail/sagie_add_round`, data),
        createSimonaAddRoundPaypage: data=>createInstance().post('/tarotmail/simona_add_round/create_paypage', data),
        confirmSimonaAddRound: (id, paymentToken)=>createInstance().get(`/tarotmail/simona_add_round/payment/${id}/events`, {params: {paymentToken}}),
        student: {
            get: ()=>createInstance().get('/tarotmail/student/get'),
            customers: ()=>createInstance().get('/tarotmail/student/customers'),
            mailStatus: id=>createInstance().get(`/tarotmail/student/customers/mail_status/${id}`),
            downloadEmails: ()=>createInstance().get('/tarotmail/student/download_emails', {responseType: 'blob'}),
            addCustomerRound: customerId=>createInstance().post(`/tarotmail/student/add_customer_round/${customerId}`),
            updateSettings: data=>createInstance().put('/tarotmail/student/settings', data),
            sendTestMail: ({last})=>createInstance().post('/tarotmail/student/test_mail', {last}),
            getOrders: opt=>createInstance().get('/tarotmail/student/orders', {params: opt}),
            getEvents: ()=>createInstance().get('/tarotmail/student/events'),
        },
        ext: {
            testIpn: data=>createInstance().post('/tarotmail/ext/payment_complete', data),
            testAddRoundIpn: data=>createInstance().post('/tarotmail/ext/simona_add_round_payment_complete', data),
        },
    },
    admin: {
        users: ()=>createInstance().get('/admin/users'),
        updateUserPermissions: (id, permissions)=>createInstance().put(`/admin/users/${id}/permissions`, {permissions}),
        refreshToken: id=>createInstance().post(`/admin/users/${id}/refresh_token`),
        jobs: opt=>createInstance().get('/admin/metrics/jobs', {params: opt}),
        errors: opt=>createInstance().get('/admin/metrics/errors', {params: opt}),
        getStatsKeys: ()=>createInstance().get('/admin/metrics/stats/keys'),
        getStatsLatest: key=>createInstance().get('/admin/metrics/stats/latest', {params: {key}}),
        getStatsPoints: (keys, opt)=>createInstance().get('/admin/metrics/stats/points', {params: {keys, ...opt}}),
        jobTypes: ()=>createInstance().get('/admin/job_types'),
        purchases: {
            get: ()=>createInstance().get('/admin/purchases'),
            create: data=>createInstance().post('/admin/purchases', data),
            update: (id, data)=>createInstance().put(`/admin/purchases/${id}`, data),
            remove: id=>createInstance().delete(`/admin/purchases/${id}`),
        },
        tarotmailOrders: {
            createOrder: (userId, type)=>createInstance().post('/admin/tarotmail_orders/create', {userId, type}),
            getUserOrders: userId=>createInstance().get(`/admin/tarotmail_orders/user/${userId}`),
        },
    },
};

export default api;
